// Penerimaan Barang (GRN) — daftar + terima (Phase 1E-δ #3)
//
// Halaman yang paling sering dipakai Admin Gudang, dan yang paling mahal
// kalau salah: setiap konfirmasi di sini LANGSUNG MENAMBAH STOK.
//
// Prinsip alurnya: kalau barang sesuai PO, TINGGAL KLIK. Jumlah tiap baris
// sudah terisi sisanya dari backend; mengetik hanya perlu kalau kenyataannya
// berbeda. Memaksa orang mengetik ulang angka yang sudah benar justru
// memancing salah ketik pada kasus yang paling sering terjadi.
//
// NOL KONSTANTA BISNIS DI FILE INI. Toleransi over-receive, arti KURANG/
// LEBIH/SESUAI, daftar status, dan wewenang semuanya datang dari
// GET /purchasing/meta/grn dan POST /purchasing/grn/preview.
// qty_match_status TIDAK dihitung di sini — backend yang menghitung, dan
// create_grn menolak nilai yang tidak cocok dengan hitungannya.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge, toneFromMeta } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  PackageCheck, RefreshCw, TrendingUp, AlertTriangle, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatQty } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import {
  listGRNs, getGRNMeta, previewGRN, createGRN, cancelGRN, listPOs, getPO, apiErrorMessage,
} from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import { hariLalu, saringanDari, saringanSampai } from "../../lib/saringanPurchasing";

//: Cermin `URUT_SAH_GRN` di backend/services/grn_service.py.
const URUT_SAH = ["receipt_date", "receipt_number", "status", "created_at"];
//: Periode bawaan riwayat penerimaan — 90 hari terakhir (terlihat sebagai chip, bisa dihapus).
const HARI_BAWAAN = 90;
//: Status PO yang menunggu barang (panel "Menunggu diterima").
const STATUS_SIAP = ["ISSUED", "PARTIAL_RECEIVED"];
//: Cermin `URUT_SAH_PO` di backend/services/po_service.py.
const URUT_SAH_PO = ["po_date", "po_number", "status", "total_amount", "created_at"];

// Terjemahan tone backend → warna hidup di SATU tempat:
// components/etams/StatusBadge (Q68). File ini hanya meneruskan tone.
export function grnStatusBadge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

export function matchBadge(meta, code) {
  const m = (meta?.qty_match || []).find(x => x.code === code);
  return {
    label: m?.label || code || "—",
    description: m?.description || "",
    tone: toneFromMeta(m?.tone),
  };
}

const namaBahan = (m) => (m ? (m.code ? `${m.code} — ${m.name}` : m.name) : "—");

export default function Receiving() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tzForStore } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md) untuk RIWAYAT penerimaan.
  // Saringan bawaan: 90 hari terakhir. Panel "Menunggu diterima" di atas tetap antrean
  // PO berstatus terbit/diterima sebagian (antrean kecil, lihat pagarPolaDaftar).
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "receipt_date", arah: "desc" },
    saringan: {
      status: { label: "Status", tampil: (v) => grnStatusBadge(meta, v).label },
      dari: saringanDari(hariLalu(HARI_BAWAAN)),
      sampai: saringanSampai(),
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const statusFilter = daftar.nilaiSaringan.status;
  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listGRNs(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  // PO yang siap menerima barang — pekerjaan yang menunggu Admin Gudang.
  // DAFTAR-SERAGAM-SISA-KECIL-01 butir 2 (28 Sep 2026): dulu SEMUA PO terbit + diterima
  // sebagian ditarik ke browser (pindai per 200 baris). Kini daftar kedua di layar yang
  // sama (awalan alamat `siap_`), berhalaman + cari di server, 10 per halaman.
  const [poSiap, setPoSiap] = useState([]);
  const [totalSiap, setTotalSiap] = useState(0);
  const siap = useDaftar(poSiap, {
    server: { total: totalSiap, urutSah: URUT_SAH_PO },
    urutAwal: { kunci: "po_date", arah: "desc" },
    ukuran: 10,
    awalan: "siap_",
  });
  const pSiap = siap.paramServer;
  const bangunParamSiap = useCallback((ovr = {}) => ({
    status_in: STATUS_SIAP.join(","), ...(pSiap.cari && { q: pSiap.cari }),
    limit: pSiap.limit, offset: pSiap.offset, sort: pSiap.sort, arah: pSiap.arah, ...ovr,
  }), [pSiap.cari, pSiap.limit, pSiap.offset, pSiap.sort, pSiap.arah]);
  const ambilHalamanSiap = useCallback(async (off, n) =>
    (await listPOs(bangunParamSiap({ offset: off, limit: n }))).data, [bangunParamSiap]);

  // Dialog penerimaan
  const [openPo, setOpenPo] = useState(null);      // baris PO
  const [preview, setPreview] = useState(null);
  const [qty, setQty] = useState({});              // po_item_id -> string
  const [catatan, setCatatan] = useState({});      // po_item_id -> string
  // S6 lot kain: po_item_id -> [{lot_code, qty}]. KOSONG = tidak diisi, dan
  // itu SAH — backend memperlakukan lots sebagai opsional (lapis 1).
  const [lots, setLots] = useState({});

  const ubahLot = (pid, idx, ruas, nilai) => setLots(s => {
    const baris = [...(s[pid] || [])];
    baris[idx] = { ...baris[idx], [ruas]: nilai };
    return { ...s, [pid]: baris };
  });
  const tambahLot = (pid) => setLots(s => ({
    ...s, [pid]: [...(s[pid] || []), { lot_code: "", qty: "" }],
  }));
  const hapusLot = (pid, idx) => setLots(s => ({
    ...s, [pid]: (s[pid] || []).filter((_, i) => i !== idx),
  }));

  /** Cermin `resolve_item_lots` di backend — DIPERIKSA DI LAYAR supaya orangnya
   *  tahu SEKARANG, bukan lewat 400 sesudah menekan tombol.
   *
   *  ⚠ Ini BUKAN pengganti pagar backend. Backend tetap memvalidasi ulang;
   *  yang di sini cuma supaya galatnya terbaca di tempat ia bisa diperbaiki.
   *  Menyembunyikan tombol di layar bukan keamanan (CLAUDE.md). */
  const masalahLot = (pid, qtyDiterima) => {
    const baris = (lots[pid] || []).filter(l => l.lot_code || l.qty);
    if (baris.length === 0) return null;           // opsional — sah
    const kode = baris.map(l => (l.lot_code || "").trim());
    if (kode.some(k => !k)) return "Kode lot tidak boleh kosong.";
    const unik = new Set(kode.map(k => k.toLowerCase()));
    if (unik.size !== kode.length) return "Ada kode lot yang dobel dalam satu bahan.";
    if (baris.some(l => !(Number(l.qty) > 0))) return "Qty tiap lot harus lebih dari 0.";
    const total = baris.reduce((a, l) => a + Number(l.qty), 0);
    if (Math.abs(total - Number(qtyDiterima)) > 0.001) {
      return `Total lot ${total} tidak sama dengan qty diterima ${qtyDiterima}.`;
    }
    return null;
  };
  const [headerNote, setHeaderNote] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [busy, setBusy] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    getGRNMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info penerimaan.")));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listGRNs(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar penerimaan."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  const loadPoSiap = useCallback(async () => {
    try {
      const r = await listPOs(bangunParamSiap());
      setPoSiap(r.data.items || []);
      setTotalSiap(r.data.total || 0);
    } catch (e) {
      setPoSiap([]);
      setTotalSiap(0);
      toast.error(apiErrorMessage(e, "Daftar PO yang menunggu barang gagal dimuat."));
    }
  }, [bangunParamSiap]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPoSiap(); }, [loadPoSiap]);

  // Buka langsung dari halaman PO: /purchasing/receiving?po=<id>
  // PO-nya dibaca SENDIRI (bukan dicari di halaman panel yang sedang tampil — panel
  // kini berhalaman, jadi PO itu bisa saja ada di halaman lain).
  useEffect(() => {
    const poId = searchParams.get("po");
    if (!poId || openPo) return;
    // Hanya `po` yang dibuang — alamat juga menyimpan posisi daftar (halaman, cari, saringan).
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.delete("po"); return n; }, { replace: true });
    const diPanel = poSiap.find(p => p.id === poId);
    if (diPanel) { mulaiTerima(diPanel); return; }
    getPO(poId)
      .then((r) => {
        const po = r.data?.po || r.data;
        if (po && STATUS_SIAP.includes(po.status)) mulaiTerima(po);
        else toast.error("PO ini tidak sedang menunggu barang.");
      })
      .catch((e) => toast.error(apiErrorMessage(e, "PO tidak bisa dibuka.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // --- Preview: satu-satunya sumber angka & status kesesuaian ---
  const mintaPreview = useCallback(async (poId, isiQty) => {
    const body = { po_id: poId };
    if (isiQty) {
      body.items = Object.entries(isiQty).map(([po_item_id, v]) => ({
        po_item_id,
        qty_received: v === "" ? null : Number(v),
      }));
    }
    const r = await previewGRN(body);
    setPreview(r.data);
    return r.data;
  }, []);

  const mulaiTerima = async (po) => {
    setOpenPo(po);
    setPreview(null);
    setQty({});
    setCatatan({});
    setHeaderNote("");
    setReceiptDate("");
    try {
      const p = await mintaPreview(po.id, null);
      // Isi awal = default dari backend (sisa yang belum datang).
      const q = {};
      (p.items || []).forEach(b => { q[b.po_item_id] = String(b.qty_received); });
      setQty(q);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyiapkan penerimaan."));
      setOpenPo(null);
    }
  };

  // Setiap perubahan angka dihitung ulang OLEH BACKEND. UI tidak pernah
  // menebak status kesesuaian atau batas toleransi sendiri.
  const ubahQty = (poItemId, nilai) => {
    const next = { ...qty, [poItemId]: nilai };
    setQty(next);
    mintaPreview(openPo.id, next).catch(() => { /* biarkan preview lama */ });
  };

  const resetKeDefault = async () => {
    setBusy(true);
    try {
      const p = await mintaPreview(openPo.id, null);
      const q = {};
      (p.items || []).forEach(b => { q[b.po_item_id] = String(b.qty_received); });
      setQty(q);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengambil ulang jumlah PO."));
    } finally {
      setBusy(false);
    }
  };

  const submitTerima = async () => {
    if (!preview?.can_submit) return;

    // S6: hentikan di sini kalau lot-nya tidak cocok. Tanpa ini backend
    // menolak dengan 400 dan pengguna cuma melihat toast merah tanpa tahu
    // BARIS MANA yang salah — persis nasib PJ-01 percobaan pertama, waktu
    // "Network Error" muncul tanpa satu pun petunjuk.
    for (const b of (preview.items || []).filter(x => x.ok && !x.skipped)) {
      const galat = masalahLot(b.po_item_id, b.qty_received);
      if (galat) { toast.error(`${namaBahan(b.material)}: ${galat}`); return; }
    }

    const payload = (preview.items || [])
      .filter(b => b.ok && !b.skipped)
      .map(b => ({
        po_item_id: b.po_item_id,
        qty_received: b.qty_received,
        // Nilai dari backend, diteruskan apa adanya. Kalau UI mengarang
        // sendiri, create_grn menolaknya — dan seharusnya begitu.
        qty_match_status: b.qty_match_status,
        qty_match_notes: catatan[b.po_item_id] || null,
        // S6: dikirim HANYA kalau benar-benar diisi. Array kosong dan `null`
        // sama-sama sah bagi backend, tapi mengirim [] menyiratkan "sudah
        // dicek, memang nol lot" — padahal artinya "belum diisi".
        lots: (() => {
          const baris = (lots[b.po_item_id] || [])
            .filter(l => (l.lot_code || "").trim() && Number(l.qty) > 0)
            .map(l => ({ lot_code: l.lot_code.trim(), qty: Number(l.qty) }));
          return baris.length ? baris : null;
        })(),
      }));
    setBusy(true);
    try {
      await createGRN({
        po_id: openPo.id,
        items: payload,
        receipt_date: receiptDate || null,
        notes: headerNote || null,
      });
      toast.success(`Barang diterima — stok ${payload.length} bahan bertambah.`);
      setOpenPo(null);
      setPreview(null);
      load();
      loadPoSiap();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan penerimaan."));
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    setBusy(true);
    try {
      await cancelGRN(cancelTarget.id, { reason: cancelReason.trim() });
      toast.success("Penerimaan dibatalkan — stok dikembalikan.");
      setCancelTarget(null);
      setCancelReason("");
      load();
      loadPoSiap();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal membatalkan penerimaan."));
    } finally {
      setBusy(false);
    }
  };

  const perm = meta?.permissions || {};
  const adaSaringan = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;
  const KOLOM_CSV = [
    { kunci: "receipt_number", judul: "Nomor penerimaan" },
    { kunci: "receipt_date", judul: "Tanggal", ambil: (g) => formatDate(g.receipt_date) },
    { kunci: "po", judul: "Dari PO", ambil: (g) => g.po?.po_number || "" },
    { kunci: "vendor", judul: "Vendor", ambil: (g) => g.po?.vendor?.name || "" },
    { kunci: "store", judul: "Gudang", ambil: (g) => g.store?.name || "" },
    { kunci: "status", judul: "Status", ambil: (g) => grnStatusBadge(meta, g.status).label },
  ];
  const KOLOM_CSV_SIAP = [
    { kunci: "po_number", judul: "Nomor PO" },
    { kunci: "vendor", judul: "Vendor", ambil: (po) => po.vendor?.name || "" },
    { kunci: "total_amount", judul: "Nilai" },
    { kunci: "status", judul: "Status", ambil: (po) => (po.status === "PARTIAL_RECEIVED" ? "Sebagian sudah datang" : "Menunggu barang") },
  ];
  const tolPersen = meta?.tolerance_pct != null
    ? Math.round(meta.tolerance_pct * 100) : null;

  return (
    <div className="space-y-6" data-testid="grn-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <PackageCheck className="h-6 w-6" /> Penerimaan Barang
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mengonfirmasi penerimaan berarti <strong>stok bertambah</strong> di
            gudang tujuan. Kalau barang sesuai PO, jumlahnya sudah terisi —
            tinggal konfirmasi.
          </p>
        </div>
        <Button variant="outline" size="sm" data-testid="grn-refresh"
                onClick={() => { load(); loadPoSiap(); }}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* ===== Pekerjaan yang menunggu: PO siap diterima ===== */}
      <Card className="p-4" data-testid="grn-ready-panel">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">Menunggu diterima</h2>
          <span className="text-sm text-muted-foreground" data-testid="grn-ready-total">{totalSiap} PO</span>
        </div>
        {(totalSiap > 0 || siap.cari.trim()) && (
          <PalangDaftar daftar={siap} placeholder="Cari nomor PO atau vendor…" testId="grn-ready-palang"
                        ekspor={{ nama: "po_menunggu_barang", kolom: KOLOM_CSV_SIAP, ambilHalaman: ambilHalamanSiap }} />
        )}
        {poSiap.length === 0 && siap.cari.trim() ? (
          <p className="text-sm text-muted-foreground" data-testid="grn-ready-empty-cari">
            Tidak ada PO menunggu barang yang cocok dengan pencarian ini.
          </p>
        ) : poSiap.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="grn-ready-empty">
            Tidak ada PO yang sedang menunggu barang. PO baru muncul di sini
            setelah vendornya ditetapkan (status ISSUED) —{" "}
            <button type="button" className="underline underline-offset-2"
                    onClick={() => navigate("/purchasing/po")}>
              lihat daftar PO
            </button>.
          </p>
        ) : (
          <div className="space-y-2">
            {siap.tampil.map(po => (
              <div key={po.id}
                   className="flex items-center justify-between gap-3 flex-wrap
                              border rounded p-3 hover:bg-muted/50"
                   data-testid={`grn-ready-${po.po_number}`}>
                <div className="text-sm">
                  <button type="button"
                          onClick={() => navigate(`/purchasing/po/${po.id}`)}
                          className="font-medium text-primary underline underline-offset-2 hover:opacity-80">
                    {po.po_number}
                  </button>
                  <span className="text-muted-foreground">
                    {" "}· {po.vendor?.name || "vendor —"}
                    {" "}· {formatIDR(po.total_amount)}
                    {po.status === "PARTIAL_RECEIVED" && " · sebagian sudah datang"}
                  </span>
                </div>
                {perm.receive ? (
                  <Button size="sm" onClick={() => mulaiTerima(po)} disabled={!!openPo}
                          data-testid={`grn-receive-btn-${po.po_number}`}>
                    Terima barang
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    hanya Admin Gudang yang menerima barang
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        <Paginasi daftar={siap} testId="grn-ready-paginasi" pilihanUkuran={[10, 25, 50]} />
      </Card>

      {/* ===== Riwayat penerimaan ===== */}
      <h2 className="font-semibold -mb-3">Riwayat penerimaan</h2>
      <PalangDaftar daftar={daftar} placeholder="Cari nomor penerimaan atau nomor PO…" testId="grn-palang"
                    ekspor={{ nama: "penerimaan_barang", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={statusFilter} data-testid="grn-status-filter" aria-label="Saring status"
                onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua status</option>
          {(meta?.statuses || []).map(s =>
            <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          Dari
          <Input type="date" value={daftar.nilaiSaringan.dari} data-testid="grn-date-from"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("dari", e.target.value)} />
        </label>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          sampai
          <Input type="date" value={daftar.nilaiSaringan.sampai} data-testid="grn-date-to"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("sampai", e.target.value)} />
        </label>
      </PalangDaftar>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2" data-testid="grn-loading">
            {[...Array(4)].map((_, i) =>
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm" data-testid="grn-empty">
            {adaSaringan
              ? "Tidak ada penerimaan yang cocok dengan pencarian atau saringan ini. Coba hapus saringannya — penerimaan lama ada di luar 90 hari terakhir."
              : "Belum ada penerimaan tercatat. Riwayat terisi setiap kali Admin Gudang mengonfirmasi barang datang."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <ThUrut daftar={daftar} kunci="receipt_number" className="px-4 py-3 font-medium">Nomor penerimaan</ThUrut>
                  <ThUrut daftar={daftar} kunci="receipt_date" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Dari PO</th>
                  <th className="px-4 py-3 text-left font-medium">Vendor</th>
                  <th className="px-4 py-3 text-left font-medium">Gudang</th>
                  <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {daftar.tampil.map(gr => {
                  const b = grnStatusBadge(meta, gr.status);
                  return (
                    <tr key={gr.id} className="hover:bg-muted/50"
                        data-testid={`grn-row-${gr.receipt_number}`}>
                      <td className="px-4 py-3 font-medium">{gr.receipt_number}</td>
                      <td className="px-4 py-3">{formatDate(gr.receipt_date, tzForStore(gr.store?.id || gr.store_id))}</td>
                      <td className="px-4 py-3">
                        {gr.po?.po_number ? (
                          <button type="button" data-testid={`grn-po-link-${gr.receipt_number}`}
                                  onClick={() => navigate(`/purchasing/po/${gr.po.id}`)}
                                  className="text-primary underline underline-offset-2 hover:opacity-80">
                            {gr.po.po_number}
                          </button>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {gr.po?.vendor?.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{gr.store?.name || "—"}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={b.tone} title={b.description}>{b.label}</StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {perm.cancel && gr.status === "RECEIVED" && (
                          <Button variant="ghost" size="sm"
                                  data-testid={`grn-cancel-${gr.receipt_number}`}
                                  onClick={() => { setCancelReason(""); setCancelTarget(gr); }}>
                            <RotateCcw className="h-4 w-4 mr-1" /> Batalkan
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Paginasi daftar={daftar} testId="grn-paginasi" />
      </Card>

      {/* ===== Dialog terima barang ===== */}
      <Dialog open={!!openPo} onOpenChange={(v) => { if (!v) setOpenPo(null); }}>
        <DialogContent className="max-w-3xl" data-testid="grn-receive-dialog">
          <DialogHeader>
            <DialogTitle>
              Terima barang — {openPo?.po_number}
              {openPo?.vendor?.name ? ` · ${openPo.vendor.name}` : ""}
            </DialogTitle>
          </DialogHeader>

          {!preview ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Menyiapkan…</div>
          ) : !preview.po_can_receive ? (
            <div className="text-sm bg-danger-soft text-danger-soft-foreground border border-danger/30 rounded p-3"
                 data-testid="grn-po-blocked">
              {preview.po_blocked_reason}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm bg-info-soft text-info-soft-foreground border border-info/30 rounded p-3"
                   data-testid="grn-tolerance-note">
                Jumlah di bawah sudah terisi <strong>sisa yang belum datang</strong> —
                kalau barangnya memang sesuai PO, tidak perlu diubah sama sekali.
                {tolPersen !== null && (
                  <> Kelebihan terima masih diterima sampai <strong>{tolPersen}%</strong> di
                  atas jumlah pesanan; di atas itu ditolak.</>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium">Bahan</th>
                      <th className="px-2 py-2 text-right font-medium">Dipesan</th>
                      <th className="px-2 py-2 text-right font-medium">Sudah datang</th>
                      <th className="px-2 py-2 text-right font-medium">Diterima kini</th>
                      <th className="px-2 py-2 text-right font-medium">Maks.</th>
                      <th className="px-2 py-2 text-left font-medium">Hasil</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(preview.items || []).map(b => {
                      const mb = b.qty_match_status ? matchBadge(meta, b.qty_match_status) : null;
                      return (
                        <tr key={b.po_item_id} className={b.ok ? "" : "bg-danger-soft/50"}
                            data-testid={`grn-prev-${b.po_item_id}`}>
                          <td className="px-2 py-2">{namaBahan(b.material)}</td>
                          <td className="px-2 py-2 text-right">
                            {formatQty(b.qty_ordered, b.unit_of_measure)}
                          </td>
                          <td className="px-2 py-2 text-right text-muted-foreground">
                            {b.qty_received_before}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <Input type="number" min="0" step="any"
                                   className={`h-8 w-28 text-right ${b.ok ? "" : "border-danger"}`}
                                   value={qty[b.po_item_id] ?? ""}
                                   data-testid={`grn-qty-${b.po_item_id}`}
                                   onChange={(e) => ubahQty(b.po_item_id, e.target.value)} />
                          </td>
                          <td className="px-2 py-2 text-right text-muted-foreground">
                            {b.qty_max_receivable_now}
                          </td>
                          <td className="px-2 py-2">
                            {!b.ok ? (
                              <span className="text-xs text-danger-soft-foreground flex items-start gap-1">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                {b.message}
                              </span>
                            ) : b.skipped ? (
                              <span className="text-xs text-muted-foreground">tidak diterima kali ini</span>
                            ) : (
                              <div>
                                <StatusBadge tone={mb.tone} title={mb.description}>{mb.label}</StatusBadge>
                                {b.qty_difference !== 0 && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    selisih {b.qty_difference > 0 ? "+" : ""}{formatQty(b.qty_difference, b.unit_of_measure)} — {b.message}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* S6 — baris lot, satu per bahan yang benar-benar diterima.
                        Terpisah dari baris utamanya supaya tabel tetap terbaca
                        saat lot tidak dipakai, dan supaya kolomnya nol
                        menyempitkan kolom qty yang jauh lebih sering dipakai. */}
                    {(preview.items || []).filter(b => b.ok && !b.skipped).map(b => {
                      const baris = lots[b.po_item_id] || [];
                      const galat = masalahLot(b.po_item_id, qty[b.po_item_id]);
                      return (
                        <tr key={`lot-${b.po_item_id}`} className="bg-muted/20">
                          <td colSpan={6} className="px-2 py-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-muted-foreground">
                                Lot / roll kain — <b>wajib untuk bahan kain</b>, isi kode dari label roll vendor
                              </span>
                              <Button type="button" variant="outline" size="sm"
                                      className="h-7"
                                      data-testid={`lot-add-${b.po_item_id}`}
                                      onClick={() => tambahLot(b.po_item_id)}>
                                + Lot
                              </Button>
                            </div>
                            {baris.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {baris.map((l, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <Input className="h-8 w-48" placeholder="Kode lot dari label vendor"
                                           value={l.lot_code || ""}
                                           data-testid={`lot-code-${b.po_item_id}-${i}`}
                                           onChange={(e) => ubahLot(b.po_item_id, i, "lot_code", e.target.value)} />
                                    <Input type="number" min="0" step="any"
                                           className="h-8 w-28 text-right" placeholder="Qty"
                                           value={l.qty || ""}
                                           data-testid={`lot-qty-${b.po_item_id}-${i}`}
                                           onChange={(e) => ubahLot(b.po_item_id, i, "qty", e.target.value)} />
                                    <span className="text-xs text-muted-foreground">
                                      {b.unit_of_measure}
                                    </span>
                                    <Button type="button" variant="ghost" size="sm" className="h-8"
                                            onClick={() => hapusLot(b.po_item_id, i)}>
                                      hapus
                                    </Button>
                                  </div>
                                ))}
                                {galat ? (
                                  <div className="text-xs text-danger-soft-foreground flex items-start gap-1 mt-1"
                                       data-testid={`lot-err-${b.po_item_id}`}>
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    {galat}
                                  </div>
                                ) : baris.some(l => (l.lot_code || "").trim() || l.qty) ? (
                                  <div className="text-xs text-success mt-1">
                                    Total lot cocok dengan qty diterima.
                                  </div>
                                ) : (
                                  /* Baris lot yang MASIH KOSONG nol boleh
                                     berbunyi "cocok". Ia belum diisi, bukan
                                     sudah diperiksa — dan pesan yang benar
                                     secara teknis tapi menyesatkan pembacanya
                                     lebih buruk daripada nol pesan. */
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Isi kode lot dan qty-nya, atau hapus baris ini.
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Catatan kualitas hanya ketika ada yang tidak SESUAI —
                  jangan menuntut penjelasan untuk kasus yang normal. */}
              {(preview.items || []).some(b => b.ok && !b.skipped && b.qty_match_status !== "SESUAI") && (
                <div className="space-y-2" data-testid="grn-diff-notes">
                  <div className="text-sm font-medium">Catatan untuk item yang tidak sesuai</div>
                  {(preview.items || [])
                    .filter(b => b.ok && !b.skipped && b.qty_match_status !== "SESUAI")
                    .map(b => (
                      <Input key={b.po_item_id} className="h-8"
                             placeholder={`${namaBahan(b.material)} — apa yang terjadi?`}
                             value={catatan[b.po_item_id] || ""}
                             data-testid={`grn-note-${b.po_item_id}`}
                             onChange={(e) => setCatatan(c => ({ ...c, [b.po_item_id]: e.target.value }))} />
                    ))}
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm">Tanggal terima</span>
                  <Input type="date" value={receiptDate} className="mt-1"
                         data-testid="grn-receipt-date"
                         onChange={(e) => setReceiptDate(e.target.value)} />
                  <span className="text-xs text-muted-foreground">Kosong = hari ini.</span>
                </label>
                <label className="block">
                  <span className="text-sm">Catatan penerimaan</span>
                  <Textarea rows={2} value={headerNote} className="mt-1"
                            data-testid="grn-header-note"
                            onChange={(e) => setHeaderNote(e.target.value)} />
                </label>
              </div>

              {/* Akibat, bukan tombol polos. */}
              <div className={`text-sm rounded p-3 flex items-start gap-2 ${
                    preview.can_submit
                      ? "bg-success-soft text-success-soft-foreground border border-success/30"
                      : "bg-muted border text-muted-foreground"}`}
                   data-testid="grn-effect">
                <TrendingUp className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{preview.effect?.summary}</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={resetKeDefault}
                    disabled={busy || !preview?.po_can_receive}
                    data-testid="grn-reset-default">
              Kembalikan ke jumlah PO
            </Button>
            <Button variant="outline" onClick={() => setOpenPo(null)} disabled={busy}>Batal</Button>
            <BusyButton busy={busy} disabled={!preview?.can_submit} onClick={submitTerima}
                    busyText="Menyimpan penerimaan..."
                    className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90"
                    data-testid="grn-submit">
              Konfirmasi terima — stok bertambah
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog batal ===== */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }}>
        <DialogContent data-testid="grn-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Batalkan {cancelTarget?.receipt_number}</DialogTitle>
          </DialogHeader>
          <div className="text-sm bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3">
            Membatalkan penerimaan akan <strong>mengembalikan stok</strong> yang tadi
            bertambah. Kalau bahannya sudah dipakai produksi sehingga stok tidak
            cukup, backend menolak pembatalannya.
          </div>
          <label className="block">
            <span className="text-sm">Alasan <span aria-hidden="true">*</span> (min. 3 karakter)</span>
            <Textarea rows={3} value={cancelReason} className="mt-1"
                      data-testid="grn-cancel-reason"
                      onChange={(e) => setCancelReason(e.target.value)} />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={busy}>Kembali</Button>
            <BusyButton variant="destructive" data-testid="grn-cancel-confirm"
                    busy={busy} disabled={cancelReason.trim().length < 3}
                    busyText="Membatalkan..."
                    onClick={submitCancel}>
              Batalkan penerimaan
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
